<template>
  <v-container :key="store?.ID">
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center">
        <h1 class="text-h3 mb-4">{{ store ? `${store.name} Sales` : 'All Sales' }}</h1>
        <v-spacer></v-spacer>
        <v-btn
          color="error"
          @click="handleDeleteSales"
        >
          Delete Sales (CSV)
        </v-btn>
      </div>

        <search-component
            @search="handleSearch"
            :initial-values="searchFilters"
          />

          <v-card class="mb-4">
            <v-card-title class="d-flex align-center">
              <v-checkbox
                v-if="sales.length > 0"
                v-model="selectAll"
                @update:model-value="toggleSelectAll"
                label="Select All"
                hide-details
                density="compact"
                class="mr-4"
              ></v-checkbox>
              
              <v-spacer></v-spacer>
              <v-btn
                color="error"
                :disabled="selectedSales.length === 0"
                :loading="deleting"
                class="mr-4"
                prepend-icon="mdi-delete"
                @click="confirmDelete"
              >
                Delete ({{ selectedSales.length }})
              </v-btn>
                <v-btn
                  color="purple"
                  :disabled="selectedSales.length === 0"
                  class="mr-4"
                  prepend-icon="mdi-export"
                  @click="exportSelectedSales"
                >
                  Export ({{ selectedSales.length }})
              </v-btn>
              <template v-if="store || timeFilter !== ''">
                <v-btn
                  color="deep-purple"
                  class="mr-4"
                  prepend-icon="mdi-export"
                  :loading="exporting"
                  @click="exportAllSales"
                >
                  Export All
                </v-btn>

              </template>
              <v-btn
                color="success"
                prepend-icon="mdi-export"
                :loading="exporting"
                @click="exportNewSales"
                class="mr-4"
                >
                Export New
              </v-btn>
              <v-btn-group>
                <v-btn
                  :color="timeFilter === monthFilters.next ? 'secondary' : undefined"
                  @click="timeFilter = monthFilters.next; fetchSales()"
                >
                  {{ formatMonthYear(monthFilters.next) }}
                </v-btn>
                <v-btn
                  :color="timeFilter === monthFilters.current ? 'secondary' : undefined"
                  @click="timeFilter = monthFilters.current; fetchSales()"
                >
                  {{ formatMonthYear(monthFilters.current) }}
                </v-btn>
                <v-btn
                  :color="timeFilter === monthFilters.previous ? 'secondary' : undefined"
                  @click="timeFilter = monthFilters.previous; fetchSales()"
                >
                  {{ formatMonthYear(monthFilters.previous) }}
                </v-btn>
                <v-btn
                  :color="timeFilter === '' ? 'secondary' : undefined"
                  @click="timeFilter = ''; fetchSales()"
                >
                  All
                </v-btn>
              </v-btn-group>
            </v-card-title>
          </v-card>
        <!-- Loading State -->
        <v-card v-if="loading" class="pa-4 text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <div class="text-h6 mt-4">Loading sales...</div>
        </v-card>

        <!-- Error State -->
        <v-alert
          v-else-if="error"
          type="error"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <!-- No Sales State -->
        <v-card 
          v-else-if="!loading && sales.length === 0" 
          class="pa-4 text-center"
        >
          <v-icon
            icon="mdi-store"
            size="64"
            color="grey"
          ></v-icon>
          <div class="text-h6 mt-4">No sales found</div>
          <div class="text-body-1 text-grey mt-2">
            There are currently no sales to display
          </div>
        </v-card>

        <!-- Sales List -->
        <template v-else>

          <sale-item
            v-for="sale in sales"
            :key="sale.ID"
            :sale="sale"
            :selected="selectedSales.includes(sale.ID)"
            @selection-changed="handleSelectionChanged"
            @update:sale="handleSaleUpdate"
          />

          <!-- Pagination -->
          <pagination-links
            v-if="totalPages > 1"
            :current-page="currentPage"
            :total-pages="totalPages"
            @page-change="handlePageChange"
          />

          <!-- Delete Confirmation Dialog -->
          <v-dialog v-model="showDeleteDialog" max-width="500">
            <v-card>
              <v-card-title>Confirm Delete</v-card-title>
              <v-card-text>
                Are you sure you want to delete {{ selectedSales.length }} selected sales?
                This action cannot be undone.
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="grey"
                  variant="text"
                  @click="showDeleteDialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="error"
                  @click="deleteSales"
                  :loading="deleting"
                >
                  Delete
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-col>
    </v-row>
  </v-container>
  <delete-sales-modal
    v-model="showDeleteModal"
    @success="handleDeleteSuccess"
  />
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStoresStore } from '@/stores/storesStore'
import { salesService } from '@/services/salesService'
import SaleItem from '@/components/sales/SaleItem.vue'
import SearchComponent from '@/components/common/SearchComponent.vue'
import DeleteSalesModal from '@/components/sales/DeleteSalesModal.vue'
import { createSalesCSV } from '@/utils/salesCsvExport'
import PaginationLinks from '@/components/common/PaginationLinks.vue'

const route = useRoute()
const router = useRouter()
const storesStore = useStoresStore()

// State
const sales = ref([])
const loading = ref(false)
const exporting = ref(false)
const error = ref(null)
const currentPage = ref(Number(route.query.page) || 1)
const totalPages = ref(1)
const searchFilters = ref(route.query.filters?.split(',').filter(Boolean).map(filter => {
  return filter.split(':')
}) || [])
const timeFilter = ref('')
const store = computed(() => storesStore.getStoreBySlug(route.params.store))
const selectAll = ref(false)
const selectedSales = ref([])
const deleting = ref(false)
const showDeleteDialog = ref(false)
const showDeleteModal = ref(false)

// Get current date parts
const getCurrentMonthFilter = () => {
  const now = new Date()
  return `${now.getMonth() + 1}-${now.getFullYear()}`
}

// Compute previous, current and next month filters
const monthFilters = computed(() => {
  const isAllTimeFilter = timeFilter.value === 'all' || timeFilter.value === ''
  const [month, year] = (isAllTimeFilter ? getCurrentMonthFilter() : timeFilter.value).split('-').map(Number)
  const date = new Date(year, month - 1) // month is 0-based in Date constructor

  // Previous month
  const prevDate = new Date(date)
  prevDate.setMonth(date.getMonth() - 1)
  const prevFilter = `${prevDate.getMonth() + 1}-${prevDate.getFullYear()}`

  // Next month
  const nextDate = new Date(date)
  nextDate.setMonth(date.getMonth() + 1)
  const nextFilter = `${nextDate.getMonth() + 1}-${nextDate.getFullYear()}`

  return {
    previous: prevFilter,
    current: isAllTimeFilter ? getCurrentMonthFilter() : timeFilter.value,
    next: nextFilter
  }
})

const handleSearch = (filters) => {
  searchFilters.value = Object.entries(filters).map(([key, value]) => [key, value])
  currentPage.value = 1
  fetchSales(1)
}

// Update URL with current filters
const updateUrlQuery = () => {
  const query = { 
    ...route.query,
    page: currentPage.value,
    filters: searchFilters.value.map(([key, value]) => `${key}:${value}`).join(','),
  }
  
  if (timeFilter.value !== '') {
    query.time_filter = timeFilter.value
  } else {
    delete query.time_filter
  }
  router.replace({ query })
}

// Fetch sales with filters
const fetchSales = async (page = 1) => {
  console.log('fetchSales', page)
  loading.value = true
  error.value = null
  
  try {
    const params = {
      page,
      filters: searchFilters.value.map(([key, value]) => `${key}:${value}`).join(','),
      time_filter: timeFilter.value,
      store: route.params.store === 'all' ? '' : route.params.store
    }

    const response = await salesService.getSales(params)
    sales.value = response.data
    totalPages.value = response.last_page
    currentPage.value = response.current_page
    updateUrlQuery()
  } catch (err) {
    error.value = 'Failed to load sales. Please try again later.'
    console.error('Error fetching sales:', err)
  } finally {
    loading.value = false
  }
}

// Watch for changes in filters
watch([() => route.params.store], () => {
  currentPage.value = 1
  timeFilter.value = route.params.store ? getCurrentMonthFilter() : ''
  fetchSales(1)
})

// Watch for page changes
watch(currentPage, (newPage) => {
  fetchSales(newPage)
})

// Watch for URL query changes
watch(
  () => route.query,
  (newQuery) => {
    const newPage = Number(newQuery.page) || 1
    // const newTimeFilter = newQuery.time_filter || 'all'
    
    // Only update if values are different to prevent loops
    if (newPage !== currentPage.value) {
      currentPage.value = newPage
    }
    
    // if (newTimeFilter !== timeFilter.value) {
    //   timeFilter.value = newTimeFilter
    // }
  },
  { immediate: true }
)

// Initial fetch
storesStore.fetchStores()
fetchSales()

// Selection handlers
const handleSelectionChanged = (saleId) => {
  const index = selectedSales.value.indexOf(saleId)
  if (index === -1) {
    selectedSales.value.push(saleId)
  } else {
    selectedSales.value.splice(index, 1)
  }
  // Update selectAll state
  selectAll.value = selectedSales.value.length === sales.value.length
}

const toggleSelectAll = () => {
  if (selectAll.value) {
    selectedSales.value = sales.value.map(sale => sale.ID)
  } else {
    selectedSales.value = []
  }
}

// Delete handlers
const confirmDelete = () => {
  showDeleteDialog.value = true
}

const deleteSales = async () => {
  deleting.value = true
  try {
    await salesService.deleteSales(selectedSales.value)
    selectedSales.value = []
    showDeleteDialog.value = false
    fetchSales(currentPage.value)
  } catch (err) {
    error.value = 'Failed to delete sales. Please try again later.'
    console.error('Error deleting sales:', err)
  } finally {
    deleting.value = false
  }
}

const handleSaleUpdate = async (updatedSale) => {
  try {
    await salesService.updateSale(updatedSale.ID, updatedSale)
    // Update the sale in the local list
    const index = sales.value.findIndex(s => s.ID === updatedSale.ID)
    if (index !== -1) {
      sales.value[index] = updatedSale
    }
  } catch (err) {
    error.value = 'Failed to update sale. Please try again.'
    console.error('Error updating sale:', err)
  }
}

const handleDeleteSales = () => {
  showDeleteModal.value = true
}

const handleDeleteSuccess = () => {
  // Refresh the sales list
  fetchSales()
}

const formatMonthYear = (monthYear) => {
  if (monthYear === '') return 'All'
  const [month, year] = monthYear.split('-').map(Number)
  return new Date(year, month - 1).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric'
  })
}

const exportAllSales = async () => {
  exporting.value = true
  try {
    const params = {
      filters: searchFilters.value.map(([key, value]) => `${key}:${value}`).join(','),
      time_filter: timeFilter.value,
      store: route.params.store === 'all' ? '' : route.params.store,
      export: 1
    }

    const response = await salesService.exportSales(params)
    
    // Create and trigger download
    const url = window.URL.createObjectURL(new Blob([response]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `sales-export-${new Date().toISOString().split('T')[0]}.zip`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (err) {
    error.value = 'Failed to export sales'
    console.error('Error exporting sales:', err)
  } finally {
    exporting.value = false
  }
}

const exportNewSales = async () => {
  exporting.value = true
  try {
    const response = await salesService.exportNewSales(route.params.store)
    
    // Create and trigger download
    const url = window.URL.createObjectURL(new Blob([response]))
    const link = document.createElement('a')
    link.href = url
    const storeName = store.value?.name || ''
    link.setAttribute('download', `${storeName}-new-sales-${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (err) {
    error.value = 'Failed to export new sales'
    console.error('Error exporting new sales:', err)
  } finally {
    exporting.value = false
  }
}

const exportSelectedSales = () => {
  // Get full sale objects for selected IDs
  const selectedSalesData = sales.value.filter(sale => 
    selectedSales.value.includes(sale.ID)
  )
  
  // Create CSV content
  const csvContent = createSalesCSV(selectedSalesData)
  
  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `${store.value?.name ? store.value?.name + '-' : ''}filtered-sales-${new Date().toISOString().split('T')[0]}.csv`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const handlePageChange = (newPage) => {
  currentPage.value = newPage
  fetchSales(newPage)
}
</script> 